import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Popover,
  Alert,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import {
  EventAvailable,
  PendingActions,
  EventBusy,
  Cancel,
  Add,
  SwapHoriz,
  AssignmentReturn,
  ChevronLeft,
  ChevronRight,
  CalendarMonth,
} from "@mui/icons-material";
import {
  leaveService,
  hrService,
  LeaveApplication,
  LeaveBalance,
} from "../../services/leaveService";
import ApplyLeave from "../leaves/ApplyLeave";
import EmploymentHistoryList, {
  EmploymentRecord,
} from "../shared/EmploymentHistoryList";
import { History as HistoryIcon } from "@mui/icons-material";

const EmployeeDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [leaveBalance, setLeaveBalance] = useState<LeaveBalance[]>([]);
  const [allLeaves, setAllLeaves] = useState<LeaveApplication[]>([]);
  // Use 'any' for now to match structure, can define stricter type if needed
  const [teamLeaves, setTeamLeaves] = useState<any[]>([]);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    cancelled: 0,
    returned: 0,
    pendingDays: 0,
    approvedDays: 0,
    rejectedDays: 0,
    cancelledDays: 0,
    returnedDays: 0,
  });
  const [substituteRequests, setSubstituteRequests] = useState<any[]>([]);
  const [mySubstitutions, setMySubstitutions] = useState<any[]>([]);
  const [processingSub, setProcessingSub] = useState<number | null>(null);
  const [employmentHistory, setEmploymentHistory] = useState<
    EmploymentRecord[]
  >([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [uiNotification, setUiNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "info" });

  // Calendar state
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState<{
    [key: string]: { leaves: any[]; substitutions: any[]; team: any[] };
  }>({});

  // Leave list dialog state
  const [leaveListOpen, setLeaveListOpen] = useState(false);
  const [leaveListTitle, setLeaveListTitle] = useState("");
  const [leaveListData, setLeaveListData] = useState<LeaveApplication[]>([]);

  // Calendar popover state
  const [calendarPopover, setCalendarPopover] = useState<{
    anchorEl: HTMLElement | null;
    date: string;
    leaves: any[];
    substitutions: any[];
    team: any[];
  }>({ anchorEl: null, date: "", leaves: [], substitutions: [], team: [] });

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    buildCalendarData();
  }, [allLeaves, mySubstitutions, teamLeaves, calendarDate]);

  const loadDashboardData = async () => {
    try {
      const [
        balanceData,
        leavesData,
        substituteRequestsData,
        mySubstitutionsData,
        teamLeavesData,
      ] = await Promise.all([
        leaveService.getLeaveBalance(),
        leaveService.getMyLeaves(),
        leaveService.getPendingSubstituteRequests(),
        leaveService.getMySubstitutions(),
        hrService.getTeamCalendar(),
      ]);

      setLeaveBalance(balanceData);
      setAllLeaves(leavesData);
      setSubstituteRequests(substituteRequestsData);
      setMySubstitutions(mySubstitutionsData);
      setTeamLeaves(teamLeavesData || []);

      // Fetch employment history
      setLoadingHistory(true);
      try {
        const historyData = await hrService.getEmploymentHistory();
        setEmploymentHistory(historyData);
      } catch (err) {
        console.error("Failed to load employment history:", err);
      } finally {
        setLoadingHistory(false);
      }

      // Filter for current year stats
      const currentYear = new Date().getFullYear();
      const currentYearLeaves = leavesData.filter((leave) => {
        const leaveYear = new Date(leave.start_date).getFullYear();
        return leaveYear === currentYear;
      });

      // Calculate stats with days
      const pendingLeaves = currentYearLeaves.filter(
        (leave) => leave.status === "pending",
      );
      const approvedLeaves = currentYearLeaves.filter(
        (leave) => leave.status === "approved",
      );
      const rejectedLeaves = currentYearLeaves.filter(
        (leave) => leave.status === "rejected",
      );
      const returnedLeaves = currentYearLeaves.filter(
        (leave) => leave.status === "returned",
      );
      const cancelledLeaves = currentYearLeaves.filter(
        (leave) =>
          leave.status === "cancelled" ||
          leave.status === "partially_cancelled",
      );

      // Helper to count days excluding weekends
      const countDaysExcludingWeekends = (startStr: string, endStr: string) => {
        const start = new Date(startStr);
        const end = new Date(endStr);
        let count = 0;
        let current = new Date(start);
        while (current <= end) {
          const day = current.getDay();
          if (day !== 0 && day !== 6) count++; // Exclude Sunday(0) and Saturday(6)
          current.setDate(current.getDate() + 1);
        }
        return count;
      };

      const getCancelledDays = (leave: LeaveApplication) => {
        const cancelledDays =
          leave.cancelled_days !== undefined && leave.cancelled_days !== null
            ? parseFloat(String(leave.cancelled_days))
            : 0;

        if (Number.isFinite(cancelledDays) && cancelledDays > 0) {
          return cancelledDays;
        }

        if (leave.status === "cancelled") {
          return leave.number_of_days || 0;
        }

        return 0;
      };

      setStats({
        pending: pendingLeaves.length,
        approved: approvedLeaves.length,
        rejected: rejectedLeaves.length,
        cancelled: cancelledLeaves.length,
        returned: returnedLeaves.length,
        pendingDays: pendingLeaves.reduce(
          (sum, l) =>
            l.start_date && l.end_date
              ? sum + countDaysExcludingWeekends(l.start_date, l.end_date)
              : sum,
          0,
        ),
        approvedDays: approvedLeaves.reduce(
          (sum, l) =>
            l.start_date && l.end_date
              ? sum + countDaysExcludingWeekends(l.start_date, l.end_date)
              : sum,
          0,
        ),
        rejectedDays: rejectedLeaves.reduce(
          (sum, l) =>
            l.start_date && l.end_date
              ? sum + countDaysExcludingWeekends(l.start_date, l.end_date)
              : sum,
          0,
        ),
        cancelledDays: cancelledLeaves.reduce(
          (sum, l) => sum + getCancelledDays(l),
          0,
        ),
        returnedDays: returnedLeaves.reduce(
          (sum, l) =>
            l.start_date && l.end_date
              ? sum + countDaysExcludingWeekends(l.start_date, l.end_date)
              : sum,
          0,
        ),
      });
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    }
  };

  const buildCalendarData = () => {
    const data: {
      [key: string]: { leaves: any[]; substitutions: any[]; team: any[] };
    } = {};

    console.log("Building calendar data...");
    console.log("All leaves:", allLeaves);
    console.log("My substitutions:", mySubstitutions);
    console.log("Team leaves:", teamLeaves);

    // Add approved AND pending leaves to calendar
    allLeaves
      .filter(
        (leave) => leave.status === "approved" || leave.status === "pending",
      )
      .forEach((leave) => {
        console.log(
          "Processing leave:",
          leave.id,
          "Status:",
          leave.status,
          "Dates:",
          leave.start_date,
          "-",
          leave.end_date,
        );
        // Parse dates
        const start = new Date(leave.start_date);
        const end = new Date(leave.end_date);
        const current = new Date(start);

        while (current <= end) {
          const dateStr = `${current.getFullYear()}-${String(
            current.getMonth() + 1,
          ).padStart(2, "0")}-${String(current.getDate()).padStart(2, "0")}`;
          if (!data[dateStr]) {
            data[dateStr] = { leaves: [], substitutions: [], team: [] };
          }
          data[dateStr].leaves.push(leave);
          current.setDate(current.getDate() + 1);
        }
      });

    // Add accepted/notified substitutions
    mySubstitutions
      .filter(
        (sub) =>
          sub.substitute_status === "accepted" ||
          sub.substitute_status === "notified",
      )
      .forEach((sub) => {
        // Parse dates
        const start = new Date(sub.start_date);
        const end = new Date(sub.end_date);
        const current = new Date(start);

        while (current <= end) {
          const dateStr = `${current.getFullYear()}-${String(
            current.getMonth() + 1,
          ).padStart(2, "0")}-${String(current.getDate()).padStart(2, "0")}`;
          if (!data[dateStr]) {
            data[dateStr] = { leaves: [], substitutions: [], team: [] };
          }
          data[dateStr].substitutions.push(sub);
          current.setDate(current.getDate() + 1);
        }
      });

    // Add Team Leaves
    teamLeaves.forEach((leave) => {
      // Parse dates
      const start = new Date(leave.start_date);
      const end = new Date(leave.end_date);
      const current = new Date(start);

      while (current <= end) {
        const dateStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, "0")}-${String(current.getDate()).padStart(2, "0")}`;
        if (!data[dateStr]) {
          data[dateStr] = { leaves: [], substitutions: [], team: [] };
        }
        // Avoid duplicates if it's my own leave already added
        const exists = data[dateStr].leaves.some((l: any) => l.id === leave.id);
        if (!exists) {
          data[dateStr].team.push(leave);
        }
        current.setDate(current.getDate() + 1);
      }
    });

    console.log("Calendar data built:", data);
    setCalendarData(data);
  };

  const handleSubstituteAction = async (
    id: number,
    action: "accept" | "reject",
  ) => {
    try {
      setProcessingSub(id);
      await leaveService.processSubstituteAction(id, action);
      await loadDashboardData();
      setUiNotification({
        open: true,
        message:
          action === "accept"
            ? "Substitute request accepted."
            : "Substitute request rejected.",
        severity: "success",
      });
    } catch (error: any) {
      console.error("Failed to process substitute action:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to process action";
      setUiNotification({
        open: true,
        message: `Failed to process action: ${errorMessage}`,
        severity: "error",
      });
    } finally {
      setProcessingSub(null);
    }
  };

  const handleStatClick = (status: string) => {
    const filtered =
      status === "cancelled"
        ? allLeaves.filter(
            (leave) =>
              leave.status === "cancelled" ||
              leave.status === "partially_cancelled",
          )
        : allLeaves.filter((leave) => leave.status === status);
    const title =
      status === "cancelled"
        ? "Cancelled / Partially Cancelled Leave Applications"
        : `${status.charAt(0).toUpperCase() + status.slice(1)} Leave Applications`;
    setLeaveListTitle(title);
    setLeaveListData(filtered);
    setLeaveListOpen(true);
  };

  const getStatusColor = (status: string) => {
    const colors: {
      [key: string]:
        | "default"
        | "primary"
        | "secondary"
        | "error"
        | "info"
        | "success"
        | "warning";
    } = {
      pending: "warning",
      approved: "success",
      rejected: "error",
      cancelled: "default",
      partially_cancelled: "warning",
      cancellation_pending: "info",
      returned: "secondary",
    };
    return colors[status] || "default";
  };

  const handlePrevMonth = () => {
    setCalendarDate(
      new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1),
    );
  };

  const handleNextMonth = () => {
    setCalendarDate(
      new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1),
    );
  };

  const renderCalendar = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const current = new Date(startDate);

    while (current <= lastDay || days.length % 7 !== 0) {
      const dateStr = `${current.getFullYear()}-${String(
        current.getMonth() + 1,
      ).padStart(2, "0")}-${String(current.getDate()).padStart(2, "0")}`;
      const dayData = calendarData[dateStr] || {
        leaves: [],
        substitutions: [],
        team: [],
      };
      const isCurrentMonth = current.getMonth() === month;
      const isToday = current.toDateString() === new Date().toDateString();
      const isWeekend = current.getDay() === 0 || current.getDay() === 6;

      const hasApprovedLeaves = dayData.leaves.some(
        (l: any) => l.status === "approved",
      );
      const hasPendingLeaves = dayData.leaves.some(
        (l: any) => l.status === "pending",
      );
      const hasSubstitutions = dayData.substitutions.length > 0;
      const teamApproved = dayData.team.filter(
        (l: any) => l.status === "approved",
      );
      const teamPending = dayData.team.filter(
        (l: any) => l.status === "pending",
      );
      const hasTeamLeaves = dayData.team && dayData.team.length > 0;
      const hasContent =
        dayData.leaves.length > 0 || hasSubstitutions || hasTeamLeaves;

      // Debug logging for days with content
      if (hasContent) {
        console.log(
          `Date: ${dateStr}, Leaves: ${dayData.leaves.length}, Approved: ${hasApprovedLeaves}, Pending: ${hasPendingLeaves}, Subs: ${hasSubstitutions}, Team: ${hasTeamLeaves}`,
        );
      }

      days.push(
        <Box
          key={dateStr}
          sx={{
            p: 0.5,
            minHeight: 70,
            border: "1px solid #e0e0e0",
            bgcolor: isToday
              ? "#e3f2fd"
              : isWeekend
                ? "#f5f5f5"
                : isCurrentMonth
                  ? "white"
                  : "#fafafa",
            cursor: hasContent ? "pointer" : "default",
            transition: "background-color 0.2s",
            "&:hover": {
              bgcolor: hasContent ? "#f0f0f0" : "inherit",
            },
            position: "relative",
            overflow: "hidden",
          }}
          onClick={(e) => {
            if (hasContent) {
              setCalendarPopover({
                anchorEl: e.currentTarget,
                date: dateStr,
                leaves: dayData.leaves,
                substitutions: dayData.substitutions,
                team: dayData.team || [],
              });
            }
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: isToday ? "bold" : "normal",
              color: isCurrentMonth ? "inherit" : "#bdbdbd",
            }}
          >
            {current.getDate()}
          </Typography>
          {hasApprovedLeaves && (
            <Box
              sx={{
                display: "inline-block",
                bgcolor: "#2e7d32",
                color: "white",
                borderRadius: "8px",
                px: 0.5,
                py: 0.1,
                fontSize: "0.6rem",
                fontWeight: "bold",
                lineHeight: 1.4,
                mr: 0.3,
                mb: 0.3,
              }}
              title="Approved Leave"
            >
              {
                dayData.leaves.filter((l: any) => l.status === "approved")
                  .length
              }{" "}
              ✓
            </Box>
          )}
          {hasPendingLeaves && (
            <Box
              sx={{
                display: "inline-block",
                bgcolor: "#ed6c02",
                color: "white",
                borderRadius: "8px",
                px: 0.5,
                py: 0.1,
                fontSize: "0.6rem",
                fontWeight: "bold",
                lineHeight: 1.4,
                mr: 0.3,
                mb: 0.3,
              }}
              title="Pending Leave"
            >
              {dayData.leaves.filter((l: any) => l.status === "pending").length}{" "}
              ⏳
            </Box>
          )}
          {hasSubstitutions && (
            <Box
              sx={{
                display: "inline-block",
                bgcolor: "#0288d1",
                color: "white",
                borderRadius: "8px",
                px: 0.5,
                py: 0.1,
                fontSize: "0.6rem",
                fontWeight: "bold",
                lineHeight: 1.4,
                mr: 0.3,
                mb: 0.3,
              }}
              title="Substituting For"
            >
              {dayData.substitutions.length} Sub
            </Box>
          )}
          {teamApproved.length > 0 && (
            <Box
              sx={{
                display: "inline-block",
                bgcolor: "#9c27b0",
                color: "white",
                borderRadius: "8px",
                px: 0.5,
                py: 0.1,
                fontSize: "0.6rem",
                fontWeight: "bold",
                lineHeight: 1.4,
                mr: 0.3,
                mb: 0.2,
              }}
              title={`${teamApproved.length} Team Members on Approved Leave`}
            >
              {teamApproved.length} Team
            </Box>
          )}
          {teamPending.length > 0 && (
            <Box
              sx={{
                display: "inline-block",
                bgcolor: "#ce93d8",
                color: "white",
                borderRadius: "8px",
                px: 0.5,
                py: 0.1,
                fontSize: "0.6rem",
                fontWeight: "bold",
                lineHeight: 1.4,
                mb: 0.2,
              }}
              title={`${teamPending.length} Team Members with Pending Leave`}
            >
              {teamPending.length} Pend
            </Box>
          )}
        </Box>,
      );

      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  return (
    <Box>
      {/* Substitute Requests - Always show at top if any */}
      {substituteRequests.length > 0 && (
        <Card sx={{ mb: 3, border: "2px solid #ed6c02" }}>
          <CardContent>
            <Typography
              variant="h6"
              color="warning.main"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <PendingActions sx={{ mr: 1 }} />
              Substitute Requests (Action Required)
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              The following employees have requested you to be their substitute:
            </Typography>
            {substituteRequests.map((req) => (
              <Box
                key={req.id}
                sx={{
                  p: 2,
                  mb: 1,
                  backgroundColor: "#fffdf0",
                  borderRadius: 1,
                  border: "1px solid #ffe58f",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="subtitle1">
                    <strong>{req.employee_name}</strong> ({req.leave_type})
                  </Typography>
                  <Typography variant="body2">
                    {new Date(req.start_date).toLocaleDateString()} -{" "}
                    {new Date(req.end_date).toLocaleDateString()} (
                    {req.number_of_days} days)
                  </Typography>
                </Box>
                <Box>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    sx={{ mr: 1 }}
                    onClick={() => handleSubstituteAction(req.id, "reject")}
                    disabled={processingSub === req.id}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={() => handleSubstituteAction(req.id, "accept")}
                    disabled={processingSub === req.id}
                  >
                    Accept
                  </Button>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Row 1: Leave Balance */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Leave Balance
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ display: "block", mb: 2 }}
          >
            Note: Leave balances are provisional and subject to change based on
            leaves taken this year.
          </Typography>
          <Grid container spacing={2}>
            {leaveBalance.map((balance, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Typography variant="h4" color="primary" fontWeight="bold">
                    {balance.available_days}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {balance.leave_type}
                  </Typography>
                </Box>
              </Grid>
            ))}
            {leaveBalance.length === 0 && (
              <Grid item xs={12}>
                <Typography color="textSecondary" align="center">
                  No leave balance data available
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Row 2: Quick Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Quick Actions</Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                color="warning"
                startIcon={<AssignmentReturn />}
                onClick={() => handleStatClick("returned")}
              >
                Returned ({stats.returned})
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setShowApplyModal(true)}
              >
                Apply for Leave
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Row 3: Calendar and Leave Stats side by side */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Leave Calendar */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <CalendarMonth sx={{ mr: 1 }} />
                  Leave Calendar
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <IconButton onClick={handlePrevMonth} size="small">
                    <ChevronLeft />
                  </IconButton>
                  <Typography
                    variant="subtitle1"
                    sx={{ mx: 2, minWidth: 150, textAlign: "center" }}
                  >
                    {calendarDate.toLocaleDateString("en-US", {
                      month: "long",
                      year: "numeric",
                    })}
                  </Typography>
                  <IconButton onClick={handleNextMonth} size="small">
                    <ChevronRight />
                  </IconButton>
                </Box>
              </Box>

              {/* Legend */}
              <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      display: "inline-block",
                      bgcolor: "#2e7d32",
                      color: "white",
                      borderRadius: "8px",
                      px: 0.5,
                      py: 0.1,
                      fontSize: "0.6rem",
                      fontWeight: "bold",
                    }}
                  >
                    N ✓
                  </Box>
                  <Typography variant="caption">My Approved</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      display: "inline-block",
                      bgcolor: "#ed6c02",
                      color: "white",
                      borderRadius: "8px",
                      px: 0.5,
                      py: 0.1,
                      fontSize: "0.6rem",
                      fontWeight: "bold",
                    }}
                  >
                    N ⏳
                  </Box>
                  <Typography variant="caption">My Pending</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      display: "inline-block",
                      bgcolor: "#0288d1",
                      color: "white",
                      borderRadius: "8px",
                      px: 0.5,
                      py: 0.1,
                      fontSize: "0.6rem",
                      fontWeight: "bold",
                    }}
                  >
                    N Sub
                  </Box>
                  <Typography variant="caption">Substitute</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      display: "inline-block",
                      bgcolor: "#9c27b0",
                      color: "white",
                      borderRadius: "8px",
                      px: 0.5,
                      py: 0.1,
                      fontSize: "0.6rem",
                      fontWeight: "bold",
                    }}
                  >
                    N Team
                  </Box>
                  <Typography variant="caption">Team Approved</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      display: "inline-block",
                      bgcolor: "#ce93d8",
                      color: "white",
                      borderRadius: "8px",
                      px: 0.5,
                      py: 0.1,
                      fontSize: "0.6rem",
                      fontWeight: "bold",
                    }}
                  >
                    N Pend
                  </Box>
                  <Typography variant="caption">Team Pending</Typography>
                </Box>
              </Box>

              {/* Calendar Grid */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gap: 0.5,
                }}
              >
                {/* Day headers */}
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                  (day) => (
                    <Box
                      key={day}
                      sx={{
                        p: 0.5,
                        textAlign: "center",
                        fontWeight: "bold",
                        bgcolor: "#f5f5f5",
                        fontSize: "0.75rem",
                      }}
                    >
                      {day}
                    </Box>
                  ),
                )}

                {/* Calendar days */}
                {renderCalendar()}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Leave Stats */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              height: "100%",
            }}
          >
            {/* Returned */}
            <Card
              sx={{
                flex: 1,
                cursor: "pointer",
                transition: "all 0.2s",
                border:
                  stats.returned > 0
                    ? "2px solid #ed6c02"
                    : "1px solid transparent",
                "&:hover": { transform: "translateY(-2px)", boxShadow: 3 },
              }}
              onClick={() => handleStatClick("returned")}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <AssignmentReturn
                    color="secondary"
                    sx={{ mr: 2, fontSize: 40 }}
                  />
                  <Box>
                    <Typography variant="h4" component="div">
                      {stats.returned}
                    </Typography>
                    <Typography color="textSecondary">
                      Returned ({stats.returnedDays} days)
                    </Typography>
                    {stats.returned > 0 && (
                      <Typography variant="caption" color="warning.main">
                        Action required
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Pending */}
            <Card
              sx={{
                flex: 1,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": { transform: "translateY(-2px)", boxShadow: 3 },
              }}
              onClick={() => handleStatClick("pending")}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <PendingActions
                    color="warning"
                    sx={{ mr: 2, fontSize: 40 }}
                  />
                  <Box>
                    <Typography variant="h4" component="div">
                      {stats.pending}
                    </Typography>
                    <Typography color="textSecondary">
                      Pending ({stats.pendingDays} days)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Approved */}
            <Card
              sx={{
                flex: 1,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": { transform: "translateY(-2px)", boxShadow: 3 },
              }}
              onClick={() => handleStatClick("approved")}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <EventAvailable
                    color="success"
                    sx={{ mr: 2, fontSize: 40 }}
                  />
                  <Box>
                    <Typography variant="h4" component="div">
                      {stats.approved}
                    </Typography>
                    <Typography color="textSecondary">
                      Approved ({stats.approvedDays} days)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Rejected */}
            <Card
              sx={{
                flex: 1,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": { transform: "translateY(-2px)", boxShadow: 3 },
              }}
              onClick={() => handleStatClick("rejected")}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <EventBusy color="error" sx={{ mr: 2, fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" component="div">
                      {stats.rejected}
                    </Typography>
                    <Typography color="textSecondary">
                      Rejected ({stats.rejectedDays} days)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Cancelled */}
            <Card
              sx={{
                flex: 1,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": { transform: "translateY(-2px)", boxShadow: 3 },
              }}
              onClick={() => handleStatClick("cancelled")}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Cancel color="disabled" sx={{ mr: 2, fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" component="div">
                      {stats.cancelled}
                    </Typography>
                    <Typography color="textSecondary">
                      Cancelled ({stats.cancelledDays} days)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* My Substitutions - Last */}
      {mySubstitutions.length > 0 && (
        <Card sx={{ mb: 3, border: "1px solid #1976d2" }}>
          <CardContent>
            <Typography
              variant="h6"
              color="primary"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <SwapHoriz sx={{ mr: 1 }} />I am Substituting for:
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              You are acting as substitute for the following leaves:
            </Typography>
            {mySubstitutions
              .filter(
                (sub) =>
                  new Date(sub.end_date) >=
                  new Date(new Date().setHours(0, 0, 0, 0)),
              )
              .map((sub) => (
                <Box
                  key={sub.id}
                  sx={{
                    p: 2,
                    mb: 1,
                    backgroundColor:
                      sub.status === "approved" ? "#f0fff4" : "#fffdf0",
                    borderRadius: 1,
                    border:
                      sub.status === "approved"
                        ? "1px solid #95de64"
                        : "1px solid #ffe58f",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1">
                      <strong>{sub.employee_name}</strong> ({sub.leave_type})
                    </Typography>
                    <Typography variant="body2">
                      {new Date(sub.start_date).toLocaleDateString()} -{" "}
                      {new Date(sub.end_date).toLocaleDateString()} (
                      {sub.number_of_days} days)
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {sub.department_name}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 0.5,
                    }}
                  >
                    <Chip
                      label={`Leave: ${
                        sub.status.charAt(0).toUpperCase() + sub.status.slice(1)
                      }`}
                      color={sub.status === "approved" ? "success" : "warning"}
                      size="small"
                    />
                    <Chip
                      label={`My Status: ${
                        sub.substitute_status === "notified"
                          ? "Notified"
                          : "Accepted"
                      }`}
                      variant="outlined"
                      color={
                        sub.substitute_status === "notified"
                          ? "info"
                          : "success"
                      }
                      size="small"
                    />
                  </Box>
                </Box>
              ))}
          </CardContent>
        </Card>
      )}

      {/* Employment History Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <HistoryIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">My Employment History</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {loadingHistory ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <EmploymentHistoryList history={employmentHistory} />
          )}
        </CardContent>
      </Card>

      {/* Leave List Dialog */}
      <Dialog
        open={leaveListOpen}
        onClose={() => setLeaveListOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{leaveListTitle}</DialogTitle>
        <DialogContent>
          {leaveListData.length === 0 ? (
            <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
              No leave applications found
            </Typography>
          ) : (
            <List>
              {leaveListData.map((leave, index) => (
                <React.Fragment key={leave.id}>
                  <ListItem
                    sx={{
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#f5f5f5" },
                    }}
                    onClick={() => {
                      setLeaveListOpen(false);
                      navigate("/leaves/" + leave.id);
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="subtitle1">
                            {leave.leave_type}
                          </Typography>
                          <Chip
                            label={
                              leave.status.charAt(0).toUpperCase() +
                              leave.status.slice(1)
                            }
                            color={getStatusColor(leave.status)}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" component="span">
                            {new Date(leave.start_date).toLocaleDateString()} -{" "}
                            {new Date(leave.end_date).toLocaleDateString()}{" "}
                            {leave.number_of_days} days
                          </Typography>
                          {leave.reason && (
                            <Typography
                              variant="body2"
                              component="div"
                              color="textSecondary"
                            >
                              {leave.reason.length > 100
                                ? leave.reason.substring(0, 100) + "..."
                                : leave.reason}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                  {index < leaveListData.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveListOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Calendar Day Popover */}
      <Popover
        open={Boolean(calendarPopover.anchorEl)}
        anchorEl={calendarPopover.anchorEl}
        onClose={() =>
          setCalendarPopover({
            anchorEl: null,
            date: "",
            leaves: [],
            substitutions: [],
            team: [],
          })
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box sx={{ p: 2, minWidth: 280, maxWidth: 350 }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ mb: 1.5, borderBottom: "1px solid #e0e0e0", pb: 1 }}
          >
            {calendarPopover.date &&
              new Date(calendarPopover.date + "T00:00:00").toLocaleDateString(
                "en-US",
                {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                },
              )}
          </Typography>

          {calendarPopover.leaves.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="textSecondary"
                fontWeight="bold"
                sx={{ textTransform: "uppercase" }}
              >
                My Leaves
              </Typography>
              {calendarPopover.leaves.map((leave: any, idx: number) => (
                <Box
                  key={idx}
                  sx={{
                    mt: 1,
                    p: 1.5,
                    bgcolor:
                      leave.status === "approved" ? "#f6ffed" : "#fffbe6",
                    borderRadius: 1,
                    borderLeft: `3px solid ${
                      leave.status === "approved" ? "#52c41a" : "#faad14"
                    }`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" fontWeight="medium">
                      {leave.leave_type}
                    </Typography>
                    <Chip
                      label={
                        leave.status.charAt(0).toUpperCase() +
                        leave.status.slice(1)
                      }
                      size="small"
                      color={
                        leave.status === "approved" ? "success" : "warning"
                      }
                    />
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    {new Date(leave.start_date).toLocaleDateString()} -{" "}
                    {new Date(leave.end_date).toLocaleDateString()}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {calendarPopover.substitutions.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="caption"
                color="textSecondary"
                fontWeight="bold"
                sx={{ textTransform: "uppercase" }}
              >
                Substituting For
              </Typography>
              {calendarPopover.substitutions.map((sub: any, idx: number) => (
                <Box
                  key={idx}
                  sx={{
                    mt: 1,
                    p: 1.5,
                    bgcolor: "#e6f7ff",
                    borderRadius: 1,
                    borderLeft: "3px solid #1890ff",
                  }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    {sub.employee_name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {sub.leave_type} •{" "}
                    {sub.substitute_status === "notified"
                      ? "Notified"
                      : "Accepted"}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {calendarPopover.team && calendarPopover.team.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="caption"
                color="textSecondary"
                fontWeight="bold"
                sx={{ textTransform: "uppercase" }}
              >
                Team Leaves
              </Typography>
              {calendarPopover.team.map((leave: any, idx: number) => (
                <Box
                  key={idx}
                  sx={{
                    mt: 1,
                    p: 1,
                    bgcolor:
                      leave.status === "approved" ? "#f3e5f5" : "#fcf3f9",
                    borderRadius: 1,
                    borderLeft: `3px solid ${leave.status === "approved" ? "#9c27b0" : "#ce93d8"}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" fontWeight="medium">
                      {leave.employee_name}
                    </Typography>
                    <Chip
                      label={
                        leave.status === "approved" ? "Approved" : "Pending"
                      }
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        bgcolor:
                          leave.status === "approved" ? "#9c27b0" : "#ce93d8",
                        color: "white",
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    {leave.leave_type}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Popover>

      {/* Apply Leave Modal */}
      {showApplyModal && (
        <ApplyLeave
          onClose={() => setShowApplyModal(false)}
          onSuccess={() => {
            setShowApplyModal(false);
            loadDashboardData();
            setUiNotification({
              open: true,
              message: "Leave application submitted successfully.",
              severity: "success",
            });
          }}
        />
      )}

      <Snackbar
        open={uiNotification.open}
        autoHideDuration={3000}
        onClose={() => setUiNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={uiNotification.severity}
          variant="filled"
          onClose={() =>
            setUiNotification((prev) => ({ ...prev, open: false }))
          }
          sx={{ width: "100%" }}
        >
          {uiNotification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmployeeDashboard;
